// 导入必要的React钩子和组件
import React, { useEffect, useState, useCallback } from "react";
// 导入甘特图组件
import { Gantt, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
// 导入xlsx用于导出Excel
import * as XLSX from "xlsx";
// 带登录鉴权的施工 API 请求
import constructionApi from "../../utils/constructionApi";
import { useParams } from "react-router-dom";
// 导入侧边栏组件
import Sidebar from "../../components/Sidebar";
// 导入自定义甘特图样式
import "./gantt.css";

// 2026年加拿大安大略省法定假日列表
const HOLIDAYS_2026 = [
  "2026-01-01", // New Year's Day
  "2026-02-16", // Family Day
  "2026-04-03", // Good Friday
  "2026-05-18", // Victoria Day
  "2026-07-01", // Canada Day
  "2026-09-07", // Labour Day
  "2026-10-12", // Thanksgiving
  "2026-12-25", // Christmas Day
  "2026-12-26", // Boxing Day
];

// 计算工作日天数（排除周六、周日和法定假日）
const calculateWorkingDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // 重置时间部分，只比较日期
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  let workingDays = 0;
  const current = new Date(start);
  
  while (current <= end) {
    const dayOfWeek = current.getDay();
    // 格式化当前日期为 YYYY-MM-DD 以便匹配假日列表
    const dateStr = current.toISOString().split('T')[0];
    
    // 排除周日(0)、周六(6)和假日列表中的日期
    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !HOLIDAYS_2026.includes(dateStr)) {
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return workingDays;
};

// 自定义Tooltip组件，显示工作日天数
const CustomTooltip = ({ task }) => {
  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  };
  
  const workingDays = calculateWorkingDays(task.start, task.end);
  
  return (
    <div
      style={{
        background: "white",
        padding: "8px 12px",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        fontSize: "12px",
        minWidth: "180px"
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
        {task.name}: {formatDate(task.start)} - {formatDate(task.end)}
      </div>
      <div style={{ color: "#666" }}>
        Duration: {workingDays} working day{workingDays !== 1 ? "s" : ""}
      </div>
      {task.progress > 0 && (
        <div style={{ color: "#666" }}>
          Progress: {task.progress}%
        </div>
      )}
    </div>
  );
};

function formatDateInput(date) {
  if (!date) return "";
  const d = new Date(date);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}
function formatDateForSave(date) {
  if (!date) return "";
  const d = new Date(date);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

const TaskEditModal = ({
  task,
  allTasks,
  onClose,
  onSave,
  onDelete,
  onChange,
  error,
}) => {
  if (!task) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.3)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          minWidth: 340,
          boxShadow: "0 2px 8px #0002",
        }}
      >
        <h3 className="text-lg font-bold mb-2">Edit Task</h3>
        {error && <p role="alert" className="text-red-600 mb-2">{error}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className="mb-2">
            <label className="block mb-1">Name</label>
            <input
              className="border rounded px-2 py-1 w-full"
              value={task.name}
              onChange={(e) => onChange({ ...task, name: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="block mb-1">Progress (0-100)</label>
            <input
              type="number"
              min={0}
              max={100}
              className="border rounded px-2 py-1 w-full"
              value={task.progress}
              onChange={(e) =>
                onChange({ ...task, progress: Number(e.target.value) })
              }
            />
          </div>
          <div className="mb-2">
            <label className="block mb-1">Start Date</label>
            <input
              type="date"
              className="border rounded px-2 py-1 w-full"
              value={formatDateInput(task.start)}
              onChange={(e) =>
                onChange({
                  ...task,
                  start: e.target.value
                    ? new Date(e.target.value + "T00:00:00")
                    : null,
                })
              }
            />
          </div>
          <div className="mb-2">
            <label className="block mb-1">End Date</label>
            <input
              type="date"
              className="border rounded px-2 py-1 w-full"
              value={formatDateInput(task.end)}
              onChange={(e) =>
                onChange({
                  ...task,
                  end: e.target.value
                    ? new Date(e.target.value + "T00:00:00")
                    : null,
                })
              }
            />
          </div>
          <div className="mb-2">
            <label className="block mb-1">Owner (optional)</label>
            <input
              className="border rounded px-2 py-1 w-full"
              value={task.owner || ""}
              onChange={(e) => onChange({ ...task, owner: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="block mb-1">Type</label>
            <select
              className="border rounded px-2 py-1 w-full"
              value={task.type}
              onChange={(e) => onChange({ ...task, type: e.target.value })}
            >
              <option value="task">Task</option>
              <option value="milestone">Milestone</option>
              <option value="project">Project</option>
            </select>
          </div>
          <div className="mb-2">
            <label className="block mb-1">Dependencies</label>
            <select
              multiple
              className="border rounded px-2 py-1 w-full"
              value={task.dependencies || []}
              onChange={(e) =>
                onChange({
                  ...task,
                  dependencies: Array.from(
                    e.target.selectedOptions,
                    (o) => o.value,
                  ),
                })
              }
            >
              {allTasks
                .filter((t) => t.id !== task.id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              className="bg-green-600 text-white px-3 py-1 rounded"
            >
              Save
            </button>
            <button
              type="button"
              className="bg-red-500 text-white px-3 py-1 rounded"
              onClick={onDelete}
            >
              Delete
            </button>
            <button
              type="button"
              className="bg-gray-300 px-3 py-1 rounded"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ConSchedule页面主组件
const ConSchedule = () => {
  const { projectId } = useParams();
  // 任务数据状态，存储所有任务
  const [tasks, setTasks] = useState([]);
  // 甘特图加载状态
  const [ganttLoading, setGanttLoading] = useState(true);
  // 用户角色状态
  const [userRole, setUserRole] = useState(null);
  // 新任务表单数据状态
  const [newTask, setNewTask] = useState({
    task_name: "",
    schedule_start: "",
    schedule_finish: "",
  });
  // 错误信息状态
  const [error, setError] = useState(null);
  // 甘特图视图模式状态（支持月、周、日切换）
  const [viewMode, setViewMode] = useState(ViewMode.Month);
  const [editTask, setEditTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editTaskDraft, setEditTaskDraft] = useState(null);

  // 将多伦多时区的日期字符串转换为Date对象
  const parseTorontoDate = (dateStr) => {
    if (!dateStr) return new Date();
    // 将YYYY-MM-DD格式的字符串解析为多伦多时区的日期
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      // 创建UTC时间,然后显示为多伦多时间
      return new Date(dateStr + "T12:00:00");
    }
    return new Date(dateStr);
  };

  // 获取任务数据并转换为gantt-task-react格式
  const fetchTasks = useCallback(async (projectId, signal) => {
    setError(null);
    setGanttLoading(true);
    setTasks([]);
    try {
      const response = await constructionApi.get(`/api/tasks?projectId=${encodeURIComponent(projectId)}`, { signal });
      if (signal?.aborted) return;
      if (response.data) {
        const formattedTasks = response.data
          .map((task) => ({
            id: task._id,
            name: task.task_name,
            start: parseTorontoDate(task.schedule_start),
            end: parseTorontoDate(task.schedule_finish),
            type: task.type || "task",
            progress: task.progress || 0,
            dependencies: task.dependencies || [],
            owner: task.owner || "",
            styles: {
              progressColor: "#88B169",
              progressSelectedColor: "#6FA944",
              barHeight: 20,
            },
          }))
          // 按开始日期排序，如果开始日期相同则按任务名称排序
          .sort((a, b) => {
            const startDiff = a.start - b.start;
            if (startDiff !== 0) return startDiff;
            return a.name.localeCompare(b.name);
          });
        setTasks(formattedTasks);
      }
    } catch (error) {
      if (signal?.aborted) return;
      console.error("Error fetching tasks:", error);
      setError(error.message || "Failed to load tasks");
    } finally {
      if (!signal?.aborted) setGanttLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setUserRole(localStorage.getItem("userRole"));
    setEditTaskDraft(null);
    if (projectId) fetchTasks(projectId, controller.signal);
    return () => controller.abort();
  }, [projectId, fetchTasks]);

  // 将日期字符串转换为多伦多时区的ISO格式
  const toTorontoISOString = (dateStr) => {
    if (!dateStr) return "";
    // 创建日期对象 (本地时间)
    const date = new Date(dateStr + "T00:00:00");
    // 转换为多伦多时区的ISO字符串
    return date.toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour12: false,
    }).split(",")[0]; // 只取日期部分 YYYY-MM-DD
  };

  // 处理添加新任务表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // 获取当前选中的项目ID
    // 校验表单字段
    if (!projectId) {
      setError("No project selected. Please select a project first.");
      return;
    }
    if (
      !newTask.task_name ||
      !newTask.schedule_start ||
      !newTask.schedule_finish
    ) {
      setError("Please fill in all fields.");
      return;
    }
    try {
      // 转换日期为多伦多时区
      const torontoStartDate = toTorontoISOString(newTask.schedule_start);
      const torontoFinishDate = toTorontoISOString(newTask.schedule_finish);

      // 调用后端API新增任务
      const response = await constructionApi.post("/api/tasks", {
        projectId: projectId,
        task_name: newTask.task_name,
        schedule_start: torontoStartDate,
        schedule_finish: torontoFinishDate,
        progress: 0,
        dependencies: [],
      });
      if (response.data) {
        // 新增成功后刷新任务列表并重置表单
        fetchTasks(projectId);
        setNewTask({
          task_name: "",
          schedule_start: "",
          schedule_finish: "",
        });
      }
    } catch (error) {
      console.error("Error creating task:", error);
      setError(error.message || "Failed to create task");
    }
  };

  const handleTaskClick = (task) => {
    setEditTask(task);
    setEditTaskDraft({ ...task });
    setShowModal(true);
  };
  const handleSaveTask = async () => {
    if (!editTaskDraft) return;
    
    // 转换日期为多伦多时区格式
    const torontoStart = editTaskDraft.start ? 
      new Date(editTaskDraft.start).toLocaleString("en-CA", {
        timeZone: "America/Toronto",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).split(",")[0] : "";
    
    const torontoFinish = editTaskDraft.end ? 
      new Date(editTaskDraft.end).toLocaleString("en-CA", {
        timeZone: "America/Toronto",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).split(",")[0] : "";
    
    setError(null);
    try {
      await constructionApi.put(`/api/tasks/${editTaskDraft.id}`, {
        task_name: editTaskDraft.name,
        schedule_start: torontoStart,
        schedule_finish: torontoFinish,
        progress: editTaskDraft.progress,
        dependencies: editTaskDraft.dependencies,
        type: editTaskDraft.type,
        owner: editTaskDraft.owner || undefined,
      });
      setShowModal(false);
      setEditTask(null);
      setEditTaskDraft(null);
      fetchTasks(projectId);
    } catch (error) {
      setError(error.message || "Failed to save task");
    }
  };
  const handleDeleteTask = async () => {
    if (!editTaskDraft) return;
    setError(null);
    try {
      await constructionApi.delete(`/api/tasks/${editTaskDraft.id}`);
      setShowModal(false);
      setEditTask(null);
      setEditTaskDraft(null);
      fetchTasks(projectId);
    } catch (error) {
      setError(error.message || "Failed to delete task");
    }
  };
  const handleCancel = () => {
    setShowModal(false);
    setEditTask(null);
    setEditTaskDraft(null);
  };

  // 导出Excel功能
  const handleExportExcel = () => {
    try {
      if (!tasks || tasks.length === 0) {
        alert("No tasks to export.");
        return;
      }

      // 准备数据
      const exportData = tasks.map(task => ({
        "Name": task.name,
        "From": task.start ? new Date(task.start).toLocaleDateString("en-CA") : "",
        "To": task.end ? new Date(task.end).toLocaleDateString("en-CA") : ""
      }));

      // 创建工作簿和工作表
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule");

      // 生成并下载文件
      XLSX.writeFile(workbook, `Construction_Schedule_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export Excel file.");
    }
  };

  // 组件渲染
  return (
    <div className="flex h-screen">
      {/* 左侧边栏 */}
      <Sidebar
        userRole={userRole}
        projectId={projectId}
        scenario="construction"
      />
      {/* 主内容区域 */}
      <div className="flex-1 p-4 ml-[60px]">
        <h2 className="text-2xl font-bold mb-4">Construction Schedule</h2>

        {/* 添加新任务表单 */}
        <form
          onSubmit={handleSubmit}
          className="mb-6 w-full text-[12px] text-left"
        >
          <div className="flex flex-row gap-4 items-end w-full">
            <div className="flex flex-row gap-4 items-end flex-1">
              <div>
                <label className="block mb-1 font-medium text-[12px]">
                  Task Name
                </label>
                <input
                  type="text"
                  value={newTask.task_name}
                  onChange={(e) =>
                    setNewTask({ ...newTask, task_name: e.target.value })
                  }
                  className="w-44 border rounded px-3 py-1 text-[12px]"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-[12px]">
                  Start Date
                </label>
                <input
                  type="date"
                  value={newTask.schedule_start}
                  onChange={(e) =>
                    setNewTask({ ...newTask, schedule_start: e.target.value })
                  }
                  className="w-44 border rounded px-3 py-1 text-[12px]"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-[12px]">
                  End Date
                </label>
                <input
                  type="date"
                  value={newTask.schedule_finish}
                  onChange={(e) =>
                    setNewTask({ ...newTask, schedule_finish: e.target.value })
                  }
                  className="w-44 border rounded px-3 py-1 text-[12px]"
                  required
                />
              </div>
              <div className="flex items-end h-full">
                <button
                  type="submit"
                  className="text-white px-3 py-1 rounded text-[12px] h-[32px]"
                  style={{ backgroundColor: "#6FA944" }}
                  onMouseOver={(e) =>
                    (e.target.style.backgroundColor = "#5C8E37")
                  }
                  onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
                >
                  Add Task
                </button>
              </div>
              
              {/* 切换按钮组 */}
              <div className="flex items-end h-full gap-2 ml-2">
                {[
                  { mode: ViewMode.Month, label: "M" },
                  { mode: ViewMode.Week, label: "W" },
                  { mode: ViewMode.Day, label: "D" },
                ].map(({ mode, label }) => (
                  <button
                    key={label}
                    type="button"
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold border ${
                      viewMode === mode
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-gray-200 text-gray-800 border-gray-300"
                    }`}
                    style={
                      viewMode === mode ? { backgroundColor: "#6FA944" } : {}
                    }
                    onClick={() => setViewMode(mode)}
                    onMouseOver={(e) => {
                      if (viewMode !== mode)
                        e.target.style.backgroundColor = "#e5e7eb";
                    }}
                    onMouseOut={(e) => {
                      if (viewMode !== mode) e.target.style.backgroundColor = "";
                    }}
                    title={
                      label === "M" ? "Month" : label === "W" ? "Week" : "Day"
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 导出Excel按钮 - 靠右对齐 */}
            <div className="flex items-end h-full ml-auto">
              <button
                type="button"
                onClick={handleExportExcel}
                className="text-white px-3 py-1 rounded text-[12px] h-[32px] flex items-center gap-1"
                style={{ backgroundColor: "#224228" }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#1a3320")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#224228")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002 -2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Excel
              </button>
            </div>
          </div>
          {error && <div className="text-red-600 mt-2">{error}</div>}
        </form>

        {/* 甘特图显示区域 */}
        <div style={{ height: "500px" }}>
          {ganttLoading ? (
            // 加载中动画
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
              <span className="ml-3 text-[12px]">Loading Gantt chart...</span>
            </div>
          ) : tasks.length > 0 ? (
            // 有任务时渲染甘特图
            <div className="gantt-container">
              {console.log("gantt-task-react tasks:", tasks)}
              <Gantt
                tasks={tasks}
                viewMode={viewMode}
                listCellWidth={"155px"}
                columnWidth={60}
                height={450}
                rowHeight={30}
                onClick={handleTaskClick}
                TooltipContent={CustomTooltip}
              />
              <TaskEditModal
                task={editTaskDraft}
                allTasks={tasks}
                onClose={handleCancel}
                onSave={handleSaveTask}
                onDelete={handleDeleteTask}
                onChange={setEditTaskDraft}
                error={error}
              />
            </div>
          ) : (
            // 无任务时的提示
            <div className="text-gray-500 text-center mt-8 text-[12px]">
              <p>{error ? "Unable to load tasks. Please try again." : "No tasks available."}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConSchedule;
